import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  Typography,
  styled,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentAgent } from "../../redux/features/authSlice";
import AddTask from "./AddTask";
import { MuiTable } from "../../components/MuiTable";
import { useGetAllTasksQuery } from "../../redux/api/tasksApi";
import { TASK_COLUMN } from "./taskData";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useLocation } from "react-router-dom";
import { getDateRange } from "../../components/getDateValues";
import { TASK_STATUSES } from "../../constants/task";
import { DATE_RANGES } from "../../constants/datesList";
import { useGetAgentsQuery } from "../../redux/api/authApi";
import EditTask from "./EditTask";
import { TaskDetailsModal } from "./TaskDetailsModal";
import { isDisabled } from "../../helper/agentRights";

const StyledTop = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}));

export default function Tasks() {
  const currentAgent = useSelector(selectCurrentAgent);
  const { search } = useLocation();
  const [selectedTask, setSelectedTask] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const queryParams = new URLSearchParams(search);
  const urlStatus = queryParams.get("status");
  const urlRange = queryParams.get("range");
  const urlAgentId = queryParams.get("agentId");
  const taskId = queryParams.get("taskId");
  const statusId = TASK_STATUSES.find(
    (status) => status.label.toLowerCase() === urlStatus?.toLowerCase()
  )?.value;

  const [filters, setFilters] = useState({
    receiverId: urlAgentId || "",
    dateRange: urlRange?.toLowerCase().replace("-", "") || "all",
    status: statusId || "",
  });

  const [calculatedDates, setCalculatedDates] = useState({
    from: null,
    to: null,
  });

  const { data: agents } = useGetAgentsQuery(currentAgent.agencyid);
  const agentRights = currentAgent.agentrights || {};

  const canViewAllTasks = !isDisabled(agentRights, "All Tasks");
  const canAssignTask = !isDisabled(agentRights, "Assign Task");

  const { data, isLoading, refetch } = useGetAllTasksQuery({
    agcId: currentAgent.agencyid,
    from: calculatedDates.from,
    to: calculatedDates.to,
    status: filters.status,
    ...(canViewAllTasks
      ? {
          ...(filters.receiverId && { receiverId: filters.receiverId }),
        }
      : {
          senderId: currentAgent.agentid,
          receiverId: filters.receiverId,
        }),
  });

  const formatToISOWithMillis = (date) => {
    if (!date) return null;
    const isoString = date.toISOString();
    return isoString.slice(0, -1) + "000Z";
  };

  useEffect(() => {
    const { fromDate, toDate } = getDateRange(filters.dateRange);
    setCalculatedDates({
      from: formatToISOWithMillis(fromDate),
      to: formatToISOWithMillis(toDate),
    });
  }, [filters.dateRange]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleEditClick = (task) => {
    setSelectedTask(task);
    setEditOpen(true);
  };
  const handleRowClick = (row) => {
    setSelectedTask(row.original); // assuming `row.original` has the full task data
    setDetailsOpen(true);
  };
  const handleEditClose = () => {
    setEditOpen(false);
    setSelectedTask(null);
    refetch();
  };
  const handleDetailsClose = () => {
    setSelectedTask(null);
    setDetailsOpen(false);
  };
  const filteredData = React.useMemo(() => {
    if (!data) return [];
    if (taskId) {
      return data.filter(
        (task) => task.taskId?.toString() === taskId.toString()
      );
    }
    return data;
  }, [data, taskId]);
  return (
    <>
      <StyledTop>
        <Typography variant="h2">All Tasks</Typography>
        <Button
          variant="contained"
          sx={{ ml: "auto", color: "white" }}
          onClick={handleOpen}
        >
          Add Task
        </Button>
      </StyledTop>

      <Box
        sx={{
          mb: 4,
          p: 0,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <FilterAltIcon fontSize="small" />
            Filters
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {canViewAllTasks && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="agent-filter-label">Assigned To</InputLabel>
                  <Select
                    labelId="agent-filter-label"
                    value={filters.receiverId}
                    onChange={(e) =>
                      handleFilterChange("receiverId", e.target.value)
                    }
                    variant="outlined"
                    label="Assigned To"
                  >
                    <MenuItem value="">All Agents</MenuItem>
                    {agents
                      ?.filter((agent) => agent.status === 1)
                      .map((agent) => (
                        <MenuItem key={agent.agentid} value={agent.agentid}>
                          {agent.agentname}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.dateRange}
                  onChange={(e) =>
                    handleFilterChange("dateRange", e.target.value)
                  }
                  variant="outlined"
                >
                  {DATE_RANGES.map((range) => (
                    <MenuItem key={range.value} value={range.value}>
                      {range.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="task-filter-label">Status</InputLabel>
                <Select
                  labelId="task-filter-label"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  variant="outlined"
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {TASK_STATUSES.map((task) => (
                    <MenuItem key={task.value} value={task.value}>
                      {task.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <MuiTable
        data={filteredData || []}
        isLoading={isLoading}
        columns={TASK_COLUMN(handleEditClick)}
        enableColor={false}
        enableRowSelection={false}
        enableActionButton={false}
        onRowClick={handleRowClick}
      />
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <AddTask canAssignTask={canAssignTask} onClose={handleClose} />
      </Dialog>

      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <EditTask
          canAssignTask={canAssignTask}
          task={selectedTask}
          onClose={handleEditClose}
        />
      </Dialog>
      <TaskDetailsModal
        open={detailsOpen}
        onClose={handleDetailsClose}
        task={selectedTask}
      />
    </>
  );
}
